import Competence, { CompetenceItem } from "../models/Competence";
import Organisation from "../models/Organisation";
import i from "./locale/i18n";

/**
 * Deep-filter archived competences out of a tree, for pickers reading the raw
 * `organisation.competences` (the GET competence endpoints already filter
 * server side). Label resolution (e.g. `getCompetencesLabels`) must keep using
 * the UNFILTERED tree so archived names still render on existing jobs.
 */
export const filterArchivedCompetences = (competences: CompetenceItem[]): CompetenceItem[] =>
	competences
		.filter(({ archived }) => !archived)
		.map((item) => (item.items ? { ...item, items: filterArchivedCompetences(item.items) } : item));

/**
 * True when the competence key path (branch[/service[/skill]]) points at a
 * node that is archived — or no longer exists — in the organisation tree.
 */
export const isArchivedCompetencePath = (
	competences: CompetenceItem[],
	branch: string,
	service?: string,
	skill?: string
): boolean => {
	const branchItem = competences.find(({ key }) => key === branch);

	if (!branchItem) return true;
	if (branchItem.archived) return true;
	if (!service) return false;

	const serviceItem = branchItem.items?.find(({ key }) => key === service);

	if (!serviceItem) return true;
	if (serviceItem.archived) return true;
	if (!skill) return false;

	const skillItem = serviceItem.items?.find(({ key }) => key === skill);

	return !skillItem || !!skillItem.archived;
};

export const initCompetence = (competences: Competence[]) => {
	return competences?.map((item) => {
		if (!item.skills) item.skills = [];
		return item;
	});
};

export const addNewCompetence = (
	competence: Competence,
	competences: Competence[]
): Competence[] => {
	if (
		!competences.find(
			({ branch, service }) => branch === competence.branch && service === competence.service
		)
	) {
		competences.push(competence);
	} else {
		if (competence.skills?.length) {
			competences = competences.map((item) => {
				if (
					item.branch === competence.branch &&
					item.service === competence.service &&
					competence.skills
				)
					item.skills = Array.from(new Set([...(item.skills || []), ...competence.skills]));

				return item;
			});
		}
	}

	return competences;
};

export const removeCompetence = (
	competences: Competence[],
	branch: string,
	service: string,
	skill?: string
) => {
	let newCompetences = competences;

	if (skill) {
		newCompetences = competences.map((item: any) => {
			if (item.branch === branch && item.service === service) {
				if (item.skills?.length > 1) {
					item.skills = item.skills.filter((skillItem?: any) => skillItem !== skill);
				} else {
					if (item.skills?.[0] === skill) {
						item.skills = [skill];
					}
				}
			}
			return item;
		});

		newCompetences = competences?.filter((item: any) => item?.skills?.[0] !== skill);
	} else {
		newCompetences = competences?.filter(
			(item) => !(item.branch === branch && item.service === service)
		);
	}

	return newCompetences;
};

export const displayCompetences = (
	competences: CompetenceItem[],
	competence?: Competence
): string => {
	if (!competence || competence.branch === "any") {
		return i.t("translation:ALL");
	}

	const { branch, service, skills } = getCompetencesLabels(competences, competence);

	if (skills.length) {
		return `${branch} > ${service} > ${skills.join(", ")}`;
	}

	return `${branch} - ${service}`;
};

export const getCompetencesLabels = (
	competences: CompetenceItem[],
	competence: Competence,
	index?: number
): { branch: string; service: string; skills: string[] } => {
	let branchLabel!: string;
	let serviceLabel!: string;
	let skillLabels: string[] = [];

	const branch = competences.find((branch) => branch.key === competence.branch);

	if (branch) {
		branchLabel = branch.name.sv || "";

		const service = branch?.items?.find((service) => service.key === competence.service);

		if (service) {
			serviceLabel = service.name.sv || "";

			if (competence.skills) {
				service?.items?.forEach((skill) => {
					if (index !== undefined) {
						if (competence.skills?.[index as number]?.includes(skill.key))
							skillLabels.push(skill.name.sv || "");
					} else if (competence.skills?.includes(skill.key)) {
						skillLabels.push(skill.name.sv || "");
					}
				});
			}
		}
	}

	return { branch: branchLabel, service: serviceLabel, skills: skillLabels };
};

/**
 * Returns a flat list of all branches and services sorted by Swedish name.
 * @param organisation
 * @returns
 */
export const flatListServices = (organisation: Organisation) => {
	const branches = organisation.competences || [];

	let out: { branch: CompetenceItem; service: CompetenceItem }[] = [];

	for (const branch of branches) {
		for (const service of branch.items || []) {
			out.push({
				branch,
				service,
			});
		}
	}

	return out.sort((a, b) => {
		const branchComp = (a.branch.name.sv || a.branch.key).localeCompare(
			b.branch.name.sv || b.branch.key
		);
		if (branchComp === 0) {
			return (a.service.name.sv || a.service.key).localeCompare(b.service.name.sv || b.service.key);
		}
		return branchComp;
	});
};

export function getServiceName(org: Organisation | undefined, branch: string, service: string) {
	if (!org) return service;

	const b = org.competences.find((b) => b.key === branch);

	if (!b || !b.items) return service;

	const s = b.items.find((s) => s.key === service);

	return s?.name.sv || s;
}

export const getCompetenceLabelByKey = (competences: CompetenceItem[]) => {
	const labelByKey = new Map<string, string>();

	competences.forEach((item) => setLabelByKey(labelByKey, item));

	return labelByKey;
};

const setLabelByKey = (labelByKey: Map<string, string>, { key, name, items }: CompetenceItem) => {
	if (name.sv) labelByKey.set(key, name.sv);

	if (items?.length) items.forEach((item) => setLabelByKey(labelByKey, item));
};
